import { cn } from "@/lib/utils";
import React, { useState, useCallback } from "react";
import { ExternalLink } from "lucide-react";
import ClaudeCodeSetupModal from "./ClaudeCodeSetupModal";
import { useAiActionStore, AI_AGENT } from "@/store/zustand/aiActionStore";

type AuthStatus = "valid" | "expired" | "missing" | "error";

interface Props {
  isForChat?: boolean;
  authStatus?: AuthStatus;
  onCCModalStateChange?: (isOpen: boolean) => void;
  onAuthRefresh?: () => void;
}

interface CustomCSSProperties extends React.CSSProperties {
  "--border-color"?: string;
  "--bg-color"?: string;
}

const ClaudeCodeSetupIndicator: React.FC<Props> = ({
  isForChat = false,
  authStatus = "missing",
  onCCModalStateChange,
  onAuthRefresh,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const agent = useAiActionStore((state) => state.agent);

  const handleSetupClick = useCallback(() => {
    setIsModalOpen(true);
    onCCModalStateChange?.(true);
  }, [onCCModalStateChange]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    onCCModalStateChange?.(false);
  }, [onCCModalStateChange]);

  const handleSetupCompleteInternal = useCallback(() => {
    setIsModalOpen(false);
    onCCModalStateChange?.(false);
    // Trigger auth refresh to update hasClaudeCodeAuth state
    onAuthRefresh?.();
  }, [onCCModalStateChange, onAuthRefresh]);

  // Don't show the indicator if:
  // 1. Auth is valid (already connected and working)
  // 2. Auth is expired but user is not using Claude Agent
  if (authStatus === "valid") {
    return null;
  }

  if (authStatus === "expired" && agent !== AI_AGENT.CLAUDE_AGENT) {
    return null;
  }

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 border bg-secondary-n-1000 border-[#0000] [background:padding-box_var(--bg-color),border-box_var(--border-color)] transition-colors"
      style={
        {
          borderWidth: "1px 1px 0px 1px",
          borderStyle: "solid",
          borderTopLeftRadius: "var(--radius-md)",
          borderTopRightRadius: "var(--radius-md)",
          "--border-color":
            "linear-gradient(91deg, #6F55F2 0%, #C1B6F9 21.02%, #2F3035 48.54%)",
          "--bg-color": "linear-gradient(#131519, #131519)",
        } as CustomCSSProperties
      }
    >
      <span className="text-xs text-secondary-n-50 whitespace-nowrap">
        {authStatus === "expired" && agent === AI_AGENT.CLAUDE_AGENT
          ? "⏰ Claude Agent Reauth Required"
          : "🎁 Get Unlimited Claude Code for $20"}
      </span>
      <button
        className={cn(
          "text-xs font-medium text-primary-n-500 hover:text-primary-n-400 flex items-center ml-2",
        )}
        onClick={handleSetupClick}
      >
        <ExternalLink className="w-3.5 h-3.5" />
      </button>

      <ClaudeCodeSetupModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSetupComplete={handleSetupCompleteInternal}
        isForChat={isForChat}
      />
    </div>
  );
};

export default ClaudeCodeSetupIndicator;
